import { Message, Thread, AppNotification, CommunicationTemplate } from '@repo/shared-types';
import { apiTransport as router } from './transport';
import { getContext } from './core';

// Threads: archive via PATCH { archived }; mark all my unread via PATCH .../threads/:id/read; messages: read receipt via PATCH { read }. Unified send, attachments, notifications, and template CRUD are persisted — see ROUTE_DRIFT.md.
export type CommunicationsSendPayload = {
  recipientId?: string;
  recipientAddress?: string;
  channel: 'EMAIL' | 'SMS';
  templateId?: string;
  templateVariables?: Record<string, unknown>;
  body?: string;
  subject?: string;
  threadId?: string;
};

export const communicationsApi = {
  /** Queued outbound send (Message + outbox); vendor delivery runs in outbox processor. */
  send: async (payload: CommunicationsSendPayload) => {
    const context = getContext();
    return router.post<Message>('/communications/send', payload, context);
  },
  email: {
    send: async (emailData: Partial<Message>) => {
      const context = getContext();
      return router.post<Message>('/communications/email/send', emailData, context);
    },
    list: async (filters?: Record<string, unknown>) => {
      const query = filters ? '?' + new URLSearchParams(filters as Record<string, string>).toString() : '';
      return router.get<Message[]>(`/communications/email${query}`);
    },
    get: async (id: string) => router.get<Message>(`/communications/email/${id}`),
    templates: {
      list: async () => router.get<CommunicationTemplate[]>('/communications/email/templates'),
      create: async (template: Omit<CommunicationTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
        const context = getContext();
        return router.post<CommunicationTemplate>('/communications/email/templates', template, context);
      },
      update: async (id: string, updates: Partial<CommunicationTemplate>) => router.patch<CommunicationTemplate>(`/communications/email/templates/${id}`, updates),
      delete: async (id: string) => router.delete<{ success: boolean }>(`/communications/email/templates/${id}`)
    }
  },
  sms: {
    send: async (smsData: Partial<Message>) => {
      const context = getContext();
      return router.post<Message>('/communications/sms/send', smsData, context);
    },
    list: async (filters?: Record<string, unknown>) => {
      const query = filters ? '?' + new URLSearchParams(filters as Record<string, string>).toString() : '';
      return router.get<Message[]>(`/communications/sms${query}`);
    },
    get: async (id: string) => router.get<Message>(`/communications/sms/${id}`),
    templates: {
      list: async () => router.get<CommunicationTemplate[]>('/communications/sms/templates'),
      create: async (template: Omit<CommunicationTemplate, 'id' | 'createdAt' | 'updatedAt'>) => {
        const context = getContext();
        return router.post<CommunicationTemplate>('/communications/sms/templates', template, context);
      },
      update: async (id: string, updates: Partial<CommunicationTemplate>) => router.patch<CommunicationTemplate>(`/communications/sms/templates/${id}`, updates),
      delete: async (id: string) => router.delete<{ success: boolean }>(`/communications/sms/templates/${id}`)
    }
  },
  threads: {
    list: async () => router.get<Thread[]>('/communications/threads'),
    get: async (id: string) => router.get<Thread>(`/communications/threads/${id}`),
    create: async (thread: Partial<Thread>) => {
      const context = getContext();
      return router.post<Thread>('/communications/threads', thread, context);
    },
    update: async (id: string, updates: Partial<Thread>) => router.patch<Thread>(`/communications/threads/${id}`, updates),
    markAllRead: async (id: string) => {
      const context = getContext();
      return router.patch<{ updatedCount: number }>(`/communications/threads/${id}/read`, {}, context);
    },
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/communications/threads/${id}`, context);
    }
  },
  messages: {
    list: async () => router.get<Message[]>('/communications/messages'),
    get: async (id: string) => router.get<Message>(`/communications/messages/${id}`),
    create: async (msg: Partial<Message>) => {
      const context = getContext();
      return router.post<Message>('/communications/messages', msg, context);
    },
    update: async (id: string, updates: Partial<Message>) => router.patch<Message>(`/communications/messages/${id}`, updates),
    delete: async (id: string) => {
      const context = getContext();
      return router.delete<{ success: boolean }>(`/communications/messages/${id}`, context);
    }
  },
  notifications: {
    list: async () => router.get<AppNotification[]>('/communications/notifications'),
    create: async (notif: Partial<AppNotification>) => router.post<AppNotification>('/communications/notifications', notif),
    markRead: async (id: string) => router.patch<{ success: boolean }>(`/communications/notifications/${id}/mark-read`, {}),
    clearAll: async () => router.delete<{ success: boolean }>('/communications/notifications/clear-all')
  }
};

